/**
 * Heyward Design System
 * Extracted from Zolplay website (/Users/amarpuri/documents/pet/website)
 *
 * Sources: globals.css, sidebar.tsx, layout.tsx, background.tsx,
 *          mdx/Gradient.tsx, ui/button.tsx, mdx/Pricing.tsx,
 *          mdx/Capabilities.tsx, modules/fx/wordmark-grain-gradient.tsx
 */

// =============================================================================
// LIGHT MODE
// =============================================================================

export const colors = {
  bg: {
    page:     '#fafafa',       // neutral-50 — body
    content:  '#ffffff',       // white — main panel
    sidebar:  '#f5f5f4',       // stone-100 — sidebar backdrop
    elevated: '#f5f5f5',       // neutral-100 — raised surfaces
    card:     '#ffffff',       // white — cards, panels
  },

  text: {
    primary:   '#262626',      // neutral-800 — body text
    strong:    '#171717',      // neutral-900 — bold/strong (warm near-black, not pure #000)
    secondary: '#525252',      // neutral-600
    muted:     '#737373',      // neutral-500 — sidebar fg, captions
    faint:     '#a3a3a3',      // neutral-400 — placeholders
  },

  border: {
    default: '#e5e5e5',        // neutral-200 — grid lines, separators
    subtle:  'rgba(0,0,0,0.04)',
    sidebar: 'rgba(0,0,0,0.08)',
  },

  overlay: {
    ambient: 'rgba(23,23,23,0.04)', // desktop command surface — page remains readable
    modal:   'rgba(23,23,23,0.48)', // mobile sheet — unmistakable modal separation
  },

  interactive: {
    hover:    '#292524',       // stone-800
    active:   '#171717',       // neutral-900 (warm near-black)
    activeBg: '#ffffff',       // white
  },

  gradient: {
    primary: { from: '#f43f5e', to: '#6366f1' },  // rose-500 → indigo-500
    red:     { from: '#f87171', to: '#dc2626' },   // red-400 → red-600
    green:   { from: '#10b981', to: '#16a34a' },   // emerald-500 → green-600
  },

  semantic: {
    success: '#22c55e',        // green-500
    error:   '#dc2626',        // red-600
    warning: '#f59e0b',        // amber-500
    info:    '#3b82f6',        // blue-500
  },

  selection: { background: '#404040', color: '#fafafa' },

  mask: '#fefefe',             // frosted edge gradients
};

// =============================================================================
// DARK MODE
// =============================================================================

export const darkColors = {
  bg: {
    page:     '#171717',       // neutral-900
    content:  '#1a1a1a',       // custom — cards, pricing, faq, capabilities
    sidebar:  '#0c0a09',       // stone-950
    elevated: '#262626',       // neutral-800
    card:     '#1a1a1a',
  },

  text: {
    primary:   '#d4d4d4',      // neutral-300
    strong:    '#ffffff',      // white
    secondary: '#a3a3a3',      // neutral-400
    muted:     '#737373',      // neutral-500
    faint:     '#525252',      // neutral-600
  },

  border: {
    default: 'oklch(35.1% 0 0)', // ≈ #595959
    subtle:  'rgba(255,255,255,0.04)',
    sidebar: 'rgba(255,255,255,0.07)',
  },

  interactive: {
    hover:    '#f5f5f4',       // stone-100
    active:   '#ffffff',
    activeBg: '#0c0a09',       // stone-950
  },

  gradient: {
    primary: { from: '#d1fae5', to: '#60a5fa' },  // emerald-100 → blue-400
    red:     { from: '#fecaca', to: '#f87171' },   // red-200 → red-400
    green:   { from: '#bbf7d0', to: '#4ade80' },   // green-200 → green-400
    amber:   { from: '#fde68a', to: '#38bdf8' },   // amber-200 → sky-400
  },

  semantic: {
    success: '#22c55e',
    error:   '#f87171',        // red-400 (lighter in dark mode)
    warning: '#fbbf24',        // amber-400
    info:    '#60a5fa',        // blue-400
  },

  selection: { background: '#d4d4d4', color: '#171717' },

  mask: '#1a1a1a',
};

// =============================================================================
// FULL NEUTRAL SCALE
// =============================================================================

export const neutral = {
  50:  '#fafafa',
  100: '#f5f5f5',
  200: '#e5e5e5',
  300: '#d4d4d4',
  400: '#a3a3a3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0a0a0a',
};

// =============================================================================
// FULL STONE SCALE
// =============================================================================

export const stone = {
  100: '#f5f5f4',
  200: '#e7e5e4',
  300: '#d6d3d1',
  400: '#a8a29e',
  500: '#78716c',
  600: '#57534e',
  700: '#44403c',
  800: '#292524',
  900: '#1c1917',
  950: '#0c0a09',
};

// =============================================================================
// ACCENT PALETTE — all accent colors used across pages
// =============================================================================

export const accent = {
  // Rose (primary gradient start)
  rose: {
    400: '#fb7185',
    500: '#f43f5e',
    600: '#e11d48',
    700: '#be123c',  // text on rose-tinted bg — passes WCAG AA at 14px+
  },
  // Indigo (primary gradient end)
  indigo: {
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',  // text on indigo-tinted bg — passes WCAG AA at 14px+
  },
  // Red (alert accent)
  red: {
    200: '#fecaca',
    400: '#f87171',
    600: '#dc2626',
    700: '#b91c1c',  // text on red-tinted bg — passes WCAG AA at 14px+
  },
  // Green / Emerald
  emerald: {
    100: '#d1fae5',
    200: '#a7f3d0',
    500: '#10b981',
    600: '#059669',
    700: '#047857',  // text on green-tinted bg — passes WCAG AA at 14px+
    800: '#065f46',
    900: '#064e3b',
  },
  green: {
    200: '#bbf7d0',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  // Blue
  blue: {
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
  },
  // Sky
  sky: {
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
  },
  // Amber (pricing badges, highlights)
  amber: {
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',  // text on amber-tinted bg — passes WCAG AA at 14px+
    900: '#78350f',
    950: '#451a03',
  },
  // Yellow
  yellow: {
    500: '#eab308',
  },
};

// =============================================================================
// SIDEBAR PATTERN
// =============================================================================

export const sidebar = {
  pattern: {
    light: { lineColor: 'rgba(0,0,0,0.03)', fillColor: '#f5f5f4' },
    dark:  { lineColor: 'rgba(255,255,255,0.05)', fillColor: '#0c0a09' },
    size: '16px 16px',
    angle: '45deg',
  },
  getPattern: (mode = 'light') => {
    const p = mode === 'dark' ? sidebar.pattern.dark : sidebar.pattern.light;
    return {
      backgroundImage: `repeating-linear-gradient(${sidebar.pattern.angle}, ${p.lineColor} 0, ${p.lineColor} 1px, ${p.fillColor} 0, ${p.fillColor} 50%)`,
      backgroundSize: sidebar.pattern.size,
    };
  },
};

// =============================================================================
// BACKGROUND DOT GRID
// =============================================================================

export const dotGrid = {
  light: { backgroundImage: 'radial-gradient(rgba(0,0,0,0.13) 1px, transparent 1px)', backgroundSize: '32px 32px' },
  dark:  { backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)', backgroundSize: '32px 32px' },
};

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const typography = {
  fontFamily: {
    sans: 'Inter, system-ui, -apple-system, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
  },
  fontFeatures: "'salt', 'calt', 'ss02', 'zero'",
  heading: { letterSpacing: '-0.035em', fontWeight: 500 },
  body: { lineHeight: '1.75em', letterSpacing: '-0.011em' },
};

// =============================================================================
// BUTTONS
// =============================================================================

export const buttons = {
  primary: {
    light: { background: '#292524', color: '#ffffff' },
    dark:  { background: '#fafafa', color: '#292524' },
  },
  secondary: {
    light: { background: '#ffffff', color: '#292524', border: '#e5e5e5' },
    dark:  { background: '#262626', color: '#d4d4d4', border: 'oklch(35.1% 0 0)' },
  },
  subtle: {
    light: { background: '#f5f5f4', color: '#292524' },
    dark:  { background: '#1c1917', color: '#f5f5f4' },
  },
};

// =============================================================================
// SPECIAL EFFECTS
// =============================================================================

export const effects = {
  // Grain gradient (wordmark visual effect)
  grain: {
    colors: ['#29262A', '#E6E6E6', '#817F7E', '#00000000'],
    blendMode: 'hard-light',
  },
  // Frosted backdrop
  backdrop: {
    light: { background: 'rgba(255,255,255,0.80)', blur: 'blur(16px)', saturate: 'saturate(50%)' },
    dark:  { background: 'rgba(0,0,0,0.70)', blur: 'blur(16px)', saturate: 'saturate(50%)' },
  },
  // Noise texture
  noise: { opacity: 0.03, size: '182px' },
};

// =============================================================================
// PROPERTY TYPE GRADIENTS — Suburb Search card heroes
// Light Zolplay-tinted gradients per property type. Each entry is a 50→100
// wash with a 700-weight icon, plus a type-coded price pill.
//
// Pill treatment per type:
//   - `house` (most common, ~95% of listings) uses a LIGHT pill — white
//     surface with stone-800 text + subtle border — so the grid doesn't
//     accumulate a wall of dark blocks. The single dark element per card
//     is the Run Due Diligence CTA, which earns its weight as the primary
//     action.
//   - Non-house types (apartment/land/townhouse/villa) use SOLID 700-level
//     pills — those colored accents read as brand, not dark blocks, and
//     they're the minority so they don't accumulate.
// =============================================================================

export const propertyTypeGradients = {
  house: {
    from:       '#f5f5f4',         // stone-100
    to:         '#e7e5e4',         // stone-200
    icon:       '#44403c',         // stone-700
    pill:       '#ffffff',         // white surface (inverted from stone-800)
    pillText:   '#292524',         // stone-800 — high contrast on white
    pillBorder: 'rgba(0,0,0,0.08)',// subtle frame so the pill reads on the stone wash
  },
  apartment: {
    from:       '#eff6ff',         // blue-50
    to:         '#dbeafe',         // blue-100
    icon:       '#1d4ed8',         // blue-700
    pill:       '#1d4ed8',         // blue-700
    pillText:   '#ffffff',
    pillBorder: 'transparent',
  },
  land: {
    from:       '#f0fdf4',         // green-50
    to:         '#dcfce7',         // green-100
    icon:       '#15803d',         // green-700
    pill:       '#15803d',         // green-700
    pillText:   '#ffffff',
    pillBorder: 'transparent',
  },
  townhouse: {
    from:       '#eef2ff',         // indigo-50
    to:         '#e0e7ff',         // indigo-100
    icon:       '#4338ca',         // indigo-700
    pill:       '#4338ca',         // indigo-700
    pillText:   '#ffffff',
    pillBorder: 'transparent',
  },
  villa: {
    from:       '#fff1f2',         // rose-50
    to:         '#ffe4e6',         // rose-100
    icon:       '#be123c',         // rose-700
    pill:       '#be123c',         // rose-700
    pillText:   '#ffffff',
    pillBorder: 'transparent',
  },
};

// =============================================================================
// GRADIENT CSS HELPERS
// =============================================================================

export const gradientCSS = {
  primary: (mode = 'light') => {
    const g = mode === 'dark' ? darkColors.gradient.primary : colors.gradient.primary;
    return { backgroundImage: `linear-gradient(135deg, ${g.from}, ${g.to})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' };
  },
  red: (mode = 'light') => {
    const g = mode === 'dark' ? darkColors.gradient.red : colors.gradient.red;
    return { backgroundImage: `linear-gradient(135deg, ${g.from}, ${g.to})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' };
  },
  green: (mode = 'light') => {
    const g = mode === 'dark' ? darkColors.gradient.green : colors.gradient.green;
    return { backgroundImage: `linear-gradient(135deg, ${g.from}, ${g.to})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' };
  },
};
